/**
 * SettingsModule — Configuración de la plataforma.
 *
 * Secciones (menú vertical izquierdo):
 *   1. Organización — nombre, zona horaria, idioma, estadísticas
 *   2. Usuarios     — lista, invitar, editar rol, desactivar
 *   3. Seguridad    — cambio de contraseña
 *   4. Integraciones — Jira, Slack, Webhook, GitHub
 *   5. Notificaciones — toggles (UI solamente por ahora)
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { settingsApi, notificationsApi } from '../../services/api';
import type { NotificationPrefs } from '../../services/api';

// ─── Types ──────────────────────────────────────────────────────────────────

type Section = 'organization' | 'users' | 'security' | 'integrations' | 'notifications' | 'monitor' | 'ai';

interface OrgData {
  id: string; slug: string; name: string; plan_tier: string;
  max_parallel_execs: number; timezone: string; language: string;
  stats: { users: number; systems: number; cases: number; plans: number };
}

interface UserData {
  id: string; email: string; full_name: string;
  role: string; is_active: boolean; created_at: string;
}

interface Integration {
  id: string; name: string; description: string; icon: string; category: string;
  fields: { key: string; label: string; placeholder: string; type: string }[];
}

// ─── Shared UI ───────────────────────────────────────────────────────────────

const LABEL: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 700,
  color: 'var(--color-text-secondary)', textTransform: 'uppercase',
  letterSpacing: '0.05em', marginBottom: 6,
};

const INPUT: React.CSSProperties = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: '1px solid var(--color-border-secondary)',
  background: 'var(--color-background-secondary)',
  color: 'var(--color-text-primary)', fontSize: 13,
  boxSizing: 'border-box', outline: 'none',
};

const INPUT_READONLY: React.CSSProperties = {
  ...INPUT, color: 'var(--color-text-tertiary)',
  background: 'var(--color-background-tertiary)', cursor: 'not-allowed',
};

const BTN_PRIMARY: React.CSSProperties = {
  padding: '9px 20px', borderRadius: 8, border: 'none',
  background: '#3B8BD4', color: '#fff', cursor: 'pointer',
  fontSize: 13, fontWeight: 600,
};

const BTN_SECONDARY: React.CSSProperties = {
  padding: '9px 16px', borderRadius: 8,
  border: '1px solid var(--color-border-secondary)',
  background: 'transparent', cursor: 'pointer',
  fontSize: 13, color: 'var(--color-text-secondary)',
};

const BTN_DANGER: React.CSSProperties = {
  padding: '6px 12px', borderRadius: 6, border: '1px solid #F7C1C1',
  background: 'transparent', cursor: 'pointer', fontSize: 12, color: '#A32D2D',
};

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={LABEL}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function SectionTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div style={{ marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid var(--color-border-tertiary)' }}>
      <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--color-text-primary)' }}>{title}</div>
      {subtitle && <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 3 }}>{subtitle}</div>}
    </div>
  );
}

function Toast({ msg, onDone }: { msg: string; onDone: () => void }) {
  useEffect(() => { const t = setTimeout(onDone, 3000); return () => clearTimeout(t); }, []);
  return (
    <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#1a1a18', color: '#fff', padding: '10px 20px', borderRadius: 10, fontSize: 13, fontWeight: 500, zIndex: 500 }}>
      {msg}
    </div>
  );
}

function Spinner({ small }: { small?: boolean }) {
  return <div style={{ padding: small ? 8 : 20, textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 12 }}>Cargando...</div>;
}

// ─── Organization Section ────────────────────────────────────────────────────

function OrganizationSection({ onToast }: { onToast: (m: string) => void }) {
  const [org, setOrg] = useState<OrgData | null>(null);
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [language, setLanguage] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    settingsApi.getOrganization().then((d) => {
      const data = d as unknown as OrgData;
      setOrg(data);
      setName(data.name);
      setTimezone(data.timezone ?? 'UTC');
      setLanguage(data.language ?? 'es');
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await settingsApi.updateOrganization({ name, timezone, language });
      onToast('Organización actualizada ✓');
    } finally {
      setSaving(false);
    }
  };

  if (!org) return <Spinner />;

  return (
    <div>
      <SectionTitle title="Organización" subtitle="Información general del tenant y configuración de la plataforma" />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Nombre de la organización">
            <input style={INPUT} value={name} onChange={e => setName(e.target.value)} />
          </Field>
        </div>
        <Field label="Identificador (slug)" hint="No modificable">
          <input style={INPUT_READONLY} value={org.slug} readOnly />
        </Field>
        <Field label="Plan">
          <input style={INPUT_READONLY} value={`${org.plan_tier} · ${org.max_parallel_execs} paralelas`} readOnly />
        </Field>
        <Field label="Zona horaria">
          <select style={INPUT} value={timezone} onChange={e => setTimezone(e.target.value)}>
            {['UTC', 'America/Mexico_City', 'America/New_York', 'America/Los_Angeles', 'America/Bogota', 'America/Lima', 'America/Santiago', 'Europe/Madrid'].map(tz => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </Field>
        <Field label="Idioma">
          <select style={INPUT} value={language} onChange={e => setLanguage(e.target.value)}>
            <option value="es">Español</option>
            <option value="en">English</option>
          </select>
        </Field>
      </div>

      {/* Stats */}
      <div style={{ margin: '24px 0', paddingTop: 20, borderTop: '1px solid var(--color-border-tertiary)' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 12 }}>Estadísticas del tenant</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
          {[
            { label: 'Usuarios', value: org.stats.users },
            { label: 'Sistemas', value: org.stats.systems },
            { label: 'Casos', value: org.stats.cases },
            { label: 'Planes', value: org.stats.plans },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--color-background-secondary)', borderRadius: 8, padding: '14px 10px', textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-text-primary)' }}>{s.value}</div>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={save} disabled={saving} style={{ ...BTN_PRIMARY, opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Guardando...' : 'Guardar cambios'}
        </button>
      </div>
    </div>
  );
}

// ─── Users Section ───────────────────────────────────────────────────────────

const ROLE_CFG: Record<string, { label: string; color: string; bg: string }> = {
  admin:  { label: 'Admin',  color: '#185FA5', bg: '#E6F1FB' },
  tester: { label: 'Tester', color: '#0F6E56', bg: '#E1F5EE' },
  viewer: { label: 'Viewer', color: '#5F5E5A', bg: '#F1EFE8' },
};

function UsersSection({ onToast }: { onToast: (m: string) => void }) {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [editUser, setEditUser] = useState<UserData | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('tester');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const u = await settingsApi.listUsers();
      setUsers(u as unknown as UserData[]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleInvite = async () => {
    if (!inviteEmail || !inviteName) return;
    setSaving(true);
    try {
      await settingsApi.inviteUser({ email: inviteEmail, full_name: inviteName, role: inviteRole });
      setShowInvite(false); setInviteEmail(''); setInviteName(''); setInviteRole('tester');
      await load(); onToast(`Usuario ${inviteEmail} invitado ✓`);
    } catch (e: unknown) {
      onToast(`Error: ${(e as Error).message}`);
    } finally { setSaving(false); }
  };

  const handleToggleActive = async (user: UserData) => {
    await settingsApi.updateUser(user.id, { is_active: !user.is_active });
    await load(); onToast(`Usuario ${user.is_active ? 'desactivado' : 'activado'} ✓`);
  };

  const handleRoleChange = async (user: UserData, role: string) => {
    await settingsApi.updateUser(user.id, { role });
    await load(); onToast('Rol actualizado ✓');
  };

  const handleDelete = async (user: UserData) => {
    if (!window.confirm(`¿Eliminar usuario ${user.email}?`)) return;
    await settingsApi.deleteUser(user.id);
    await load(); onToast('Usuario eliminado ✓');
  };

  return (
    <div>
      <SectionTitle title="Usuarios" subtitle="Gestiona los miembros del equipo y sus permisos" />

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={() => setShowInvite(v => !v)} style={BTN_PRIMARY}>
          {showInvite ? '✕ Cancelar' : '+ Invitar usuario'}
        </button>
      </div>

      {/* Invite form */}
      {showInvite && (
        <div style={{ padding: 16, borderRadius: 12, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)', marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 14 }}>Nuevo usuario</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Nombre completo">
              <input style={INPUT} value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="Ana García" />
            </Field>
            <Field label="Email">
              <input style={INPUT} type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="ana@empresa.com" />
            </Field>
            <Field label="Rol">
              <select style={INPUT} value={inviteRole} onChange={e => setInviteRole(e.target.value)}>
                <option value="admin">Admin — acceso total</option>
                <option value="tester">Tester — crear y ejecutar casos</option>
                <option value="viewer">Viewer — solo lectura</option>
              </select>
            </Field>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 18 }}>
              <button onClick={handleInvite} disabled={saving} style={{ ...BTN_PRIMARY, width: '100%' }}>
                {saving ? 'Invitando...' : 'Crear usuario'}
              </button>
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 4 }}>
            El usuario recibirá contraseña temporal: <code>Cambiar123!</code> — debe cambiarla en su primer acceso.
          </div>
        </div>
      )}

      {/* Users table */}
      {loading ? <Spinner /> : (
        <div style={{ border: '1px solid var(--color-border-tertiary)', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--color-background-secondary)' }}>
                {['Usuario', 'Email', 'Rol', 'Estado', 'Acciones'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--color-border-tertiary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((user, i) => {
                const rc = ROLE_CFG[user.role] ?? ROLE_CFG.viewer;
                return (
                  <tr key={user.id} style={{ borderBottom: i < users.length - 1 ? '1px solid var(--color-border-tertiary)' : 'none' }}>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#185FA5', flexShrink: 0 }}>
                          {user.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                        <span style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{user.full_name}</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px', color: 'var(--color-text-secondary)' }}>{user.email}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <select
                        value={user.role}
                        onChange={e => handleRoleChange(user, e.target.value)}
                        style={{ padding: '4px 8px', borderRadius: 6, border: `1px solid ${rc.bg}`, background: rc.bg, color: rc.color, fontSize: 11, fontWeight: 700, cursor: 'pointer', outline: 'none' }}
                      >
                        <option value="admin">Admin</option>
                        <option value="tester">Tester</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: user.is_active ? '#E1F5EE' : '#F1EFE8', color: user.is_active ? '#0F6E56' : '#5F5E5A' }}>
                        {user.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => handleToggleActive(user)} style={{ ...BTN_SECONDARY, padding: '4px 10px', fontSize: 11 }}>
                          {user.is_active ? 'Desactivar' : 'Activar'}
                        </button>
                        <button onClick={() => handleDelete(user)} style={BTN_DANGER}>✕</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Security Section ────────────────────────────────────────────────────────

function SecuritySection({ onToast }: { onToast: (m: string) => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = async () => {
    setError('');
    if (next !== confirm) { setError('Las contraseñas no coinciden'); return; }
    if (next.length < 8) { setError('Mínimo 8 caracteres'); return; }
    setSaving(true);
    try {
      await settingsApi.changePassword({ current_password: current, new_password: next });
      setCurrent(''); setNext(''); setConfirm('');
      onToast('Contraseña actualizada ✓');
    } catch (e: unknown) {
      setError((e as Error).message ?? 'Error al cambiar contraseña');
    } finally { setSaving(false); }
  };

  return (
    <div>
      <SectionTitle title="Seguridad" subtitle="Gestión de contraseña y configuración de acceso" />

      <div style={{ maxWidth: 480 }}>
        <div style={{ padding: 20, borderRadius: 12, border: '1px solid var(--color-border-secondary)', marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 16 }}>Cambiar contraseña</div>
          <Field label="Contraseña actual">
            <input style={INPUT} type="password" value={current} onChange={e => setCurrent(e.target.value)} />
          </Field>
          <Field label="Nueva contraseña" hint="Mínimo 8 caracteres">
            <input style={INPUT} type="password" value={next} onChange={e => setNext(e.target.value)} />
          </Field>
          <Field label="Confirmar nueva contraseña">
            <input style={INPUT} type="password" value={confirm} onChange={e => setConfirm(e.target.value)} />
          </Field>
          {error && <div style={{ padding: '8px 12px', borderRadius: 8, background: '#FCEBEB', color: '#A32D2D', fontSize: 12, marginBottom: 12 }}>{error}</div>}
          <button onClick={handleChange} disabled={saving || !current || !next || !confirm} style={{ ...BTN_PRIMARY, opacity: (!current || !next || !confirm || saving) ? 0.6 : 1, cursor: (!current || !next || !confirm || saving) ? 'not-allowed' : 'pointer' }}>
            {saving ? 'Guardando...' : 'Actualizar contraseña'}
          </button>
        </div>

        {/* 2FA info card */}
        <div style={{ padding: 16, borderRadius: 12, border: '1px solid var(--color-border-tertiary)', background: 'var(--color-background-secondary)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ fontSize: 20 }}>🔐</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>Autenticación de dos factores</div>
            <span style={{ marginLeft: 'auto', padding: '2px 8px', borderRadius: 6, background: '#FAEEDA', color: '#854F0B', fontSize: 10, fontWeight: 700 }}>Próximamente</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
            Agrega una capa extra de seguridad con TOTP (Google Authenticator, Authy).
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Integrations Section ────────────────────────────────────────────────────

function IntegrationsSection({ onToast }: { onToast: (m: string) => void }) {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [connected, setConnected] = useState<Set<string>>(new Set());
  const [configuring, setConfiguring] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, { ok: boolean; detail?: string; account?: string }>>({});

  useEffect(() => {
    settingsApi.listIntegrations().then((d) => {
      const data = d as unknown as { integrations: Integration[]; connected: string[] };
      setIntegrations(data.integrations);
      setConnected(new Set(data.connected));
    });
  }, []);

  const handleConnect = async (integration: Integration) => {
    setSaving(true);
    try {
      await settingsApi.connectIntegration(integration.id, fields);
      setConnected(prev => new Set([...prev, integration.id]));
      setConfiguring(null); setFields({});
      onToast(`${integration.name} conectado ✓`);
    } catch (e: unknown) {
      onToast(`Error: ${(e as Error).message}`);
    } finally { setSaving(false); }
  };

  const handleDisconnect = async (id: string, name: string) => {
    if (!window.confirm(`¿Desconectar ${name}?`)) return;
    await settingsApi.disconnectIntegration(id);
    setConnected(prev => { const n = new Set(prev); n.delete(id); return n; });
    onToast(`${name} desconectado ✓`);
  };

  const handleTest = async (id: string, name: string) => {
    setTesting(id);
    try {
      const res = await settingsApi.testIntegration(id) as { ok: boolean; detail?: string; account?: string };
      setTestResults(prev => ({ ...prev, [id]: res }));
      onToast(res.ok ? `${name}: conexión OK ✓` : `${name}: ${res.detail ?? 'falló la conexión'}`);
    } catch (e: unknown) {
      onToast(`Error: ${(e as Error).message}`);
    } finally { setTesting(null); }
  };

  const categories: Record<string, string> = { issues: 'Gestión de issues', notifications: 'Notificaciones', developer: 'Developer tools' };
  const grouped = integrations.reduce<Record<string, Integration[]>>((acc, i) => {
    if (!acc[i.category]) acc[i.category] = [];
    acc[i.category].push(i);
    return acc;
  }, {});

  return (
    <div>
      <SectionTitle title="Integraciones" subtitle="Conecta herramientas externas a la plataforma" />

      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat} style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
            {categories[cat] ?? cat}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {items.map(intg => {
              const isConnected = connected.has(intg.id);
              const isConfiguring = configuring === intg.id;
              return (
                <div key={intg.id} style={{ borderRadius: 12, border: `1px solid ${isConnected ? '#9DE3C7' : 'var(--color-border-tertiary)'}`, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, background: isConnected ? '#E1F5EE22' : 'var(--color-background-primary)' }}>
                    <div style={{ fontSize: 28, flexShrink: 0 }}>{intg.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)' }}>{intg.name}</span>
                        {isConnected && <span style={{ padding: '1px 6px', borderRadius: 4, background: '#E1F5EE', color: '#0F6E56', fontSize: 10, fontWeight: 700 }}>Conectado</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}>{intg.description}</div>
                      {testResults[intg.id] && (
                        <div style={{ fontSize: 11, marginTop: 4, color: testResults[intg.id].ok ? '#0F6E56' : '#B3261E' }}>
                          {testResults[intg.id].ok
                            ? `Conexión OK${testResults[intg.id].account ? ` · ${testResults[intg.id].account}` : ''}`
                            : `Falló: ${testResults[intg.id].detail ?? 'error desconocido'}`}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      {isConnected ? (
                        <>
                          {intg.id === 'jira' && (
                            <button
                              onClick={() => handleTest(intg.id, intg.name)}
                              disabled={testing === intg.id}
                              style={{ ...BTN_SECONDARY, fontSize: 12 }}
                            >
                              {testing === intg.id ? 'Probando...' : 'Probar conexión'}
                            </button>
                          )}
                          <button onClick={() => handleDisconnect(intg.id, intg.name)} style={BTN_DANGER}>Desconectar</button>
                        </>
                      ) : (
                        <button
                          onClick={() => { setConfiguring(isConfiguring ? null : intg.id); setFields({}); }}
                          style={{ ...BTN_SECONDARY, fontSize: 12 }}
                        >
                          {isConfiguring ? '✕ Cancelar' : 'Configurar'}
                        </button>
                      )}
                    </div>
                  </div>

                  {isConfiguring && !isConnected && (
                    <div style={{ padding: '16px', borderTop: '1px solid var(--color-border-tertiary)', background: 'var(--color-background-secondary)' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        {intg.fields.map(f => (
                          <Field key={f.key} label={f.label}>
                            <input
                              style={INPUT}
                              type={f.type === 'password' ? 'password' : 'text'}
                              value={fields[f.key] ?? ''}
                              onChange={e => setFields(prev => ({ ...prev, [f.key]: e.target.value }))}
                              placeholder={f.placeholder}
                            />
                          </Field>
                        ))}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                        <button onClick={() => handleConnect(intg)} disabled={saving} style={BTN_PRIMARY}>
                          {saving ? 'Conectando...' : `Conectar ${intg.name}`}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Notifications Section ───────────────────────────────────────────────────

const NOTIFICATION_ITEMS = [
  { key: 'exec_failed',     label: 'Ejecución fallida',          desc: 'Cuando un plan de ejecución termina con errores' },
  { key: 'exec_completed',  label: 'Ejecución completada',        desc: 'Cuando un plan de ejecución finaliza exitosamente' },
  { key: 'agent_offline',   label: 'Agente desconectado',         desc: 'Cuando un agente no reporta heartbeat por más de 5 min' },
  { key: 'new_user',        label: 'Nuevo usuario invitado',      desc: 'Cuando un administrador agrega un nuevo usuario' },
  { key: 'ai_generation',   label: 'Generación IA completada',   desc: 'Cuando el generador de casos con IA termina' },
];

function NotificationsSection({ onToast }: { onToast: (m: string) => void }) {
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    notificationsApi.getPrefs().then(setPrefs).catch(() => {
      // Fallback a defaults si el endpoint falla
      setPrefs({ exec_failed: true, exec_completed: false, agent_offline: true, new_user: false, ai_generation: false });
    });
  }, []);

  const toggle = (key: keyof NotificationPrefs) => {
    if (!prefs) return;
    setPrefs(prev => prev ? { ...prev, [key]: !prev[key] } : prev);
  };

  const save = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      await notificationsApi.savePrefs(prefs);
      onToast('Preferencias de notificaciones guardadas ✓');
    } catch {
      onToast('Error guardando preferencias');
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) return <div style={{ padding: 20, color: 'var(--color-text-tertiary)', fontSize: 13 }}>Cargando...</div>;

  return (
    <div>
      <SectionTitle title="Notificaciones" subtitle="Configura qué eventos generan alertas en la plataforma" />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
        {NOTIFICATION_ITEMS.map(item => (
          <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 10, border: '1px solid var(--color-border-tertiary)', background: 'var(--color-background-primary)' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>{item.label}</div>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{item.desc}</div>
            </div>
            <div
              onClick={() => toggle(item.key as keyof NotificationPrefs)}
              style={{ width: 40, height: 22, borderRadius: 11, background: prefs[item.key as keyof NotificationPrefs] ? '#3B8BD4' : 'var(--color-border-secondary)', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
            >
              <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: prefs[item.key as keyof NotificationPrefs] ? 20 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </div>
          </div>
        ))}
      </div>

      <div style={{ padding: 14, borderRadius: 10, border: '1px solid var(--color-border-tertiary)', background: 'var(--color-background-secondary)', marginBottom: 20 }}>
        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
          💡 Las notificaciones aparecen en la campana 🔔 del encabezado. Si configuras Slack o Webhook en Integraciones, también se enviarán externamente.
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={save} disabled={saving} style={{ ...BTN_PRIMARY, opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Guardando...' : 'Guardar preferencias'}
        </button>
      </div>
    </div>
  );
}


// ─── Agent Monitor Section ───────────────────────────────────────────────────

interface LogEntry {
  id: string;
  ts: string;
  method: string;
  path: string;
  status: number;
  duration_ms: number;
  level: 'ok' | 'error';
  request: unknown;
  response: unknown;
  agent_id?: string;
}

const METHOD_COLOR: Record<string, string> = {
  GET: '#185FA5', POST: '#0F6E56', PATCH: '#7A4515', DELETE: '#7A1515',
};

const PATH_LABEL: Record<string, string> = {
  '/api/v1/agents/register': 'register',
  '/api/v1/agents/heartbeat': 'heartbeat',
  '/api/v1/agents/tasks': 'pull_tasks',
};

function pathLabel(path: string): string {
  if (PATH_LABEL[path]) return PATH_LABEL[path];
  const segs = path.split('/');
  return segs[segs.length - 1] || path;
}

function JsonViewer({ data }: { data: unknown }) {
  const [open, setOpen] = useState(false);
  if (data === null || data === undefined) return <span style={{ color: 'var(--color-text-tertiary)', fontSize: 11 }}>—</span>;
  return (
    <div>
      <button onClick={() => setOpen(v => !v)}
        style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 10, color: '#185FA5', padding: 0, fontWeight: 600 }}>
        {open ? '▾ ocultar' : '▸ ver JSON'}
      </button>
      {open && (
        <pre style={{ margin: '4px 0 0', padding: '8px 10px', borderRadius: 6, background: 'var(--color-background-primary)', border: '1px solid var(--color-border-tertiary)', fontSize: 10, overflowX: 'auto', maxHeight: 200, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}

function AgentMonitorSection() {
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [connected, setConnected] = useState(false);
  const [filterLevel, setFilterLevel] = useState<'all' | 'ok' | 'error'>('all');
  const [filterPath, setFilterPath] = useState('');
  const [paused, setPaused] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const pausedRef = useRef(false);
  pausedRef.current = paused;

  useEffect(() => {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const host = window.location.hostname;
    const ws = new WebSocket(`${proto}://${host}:8000/ws/agent-logs`);
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);

    ws.onmessage = (ev) => {
      if (pausedRef.current) return;
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === 'history') {
          setEntries(msg.entries.slice().reverse());
        } else if (msg.type === 'entry') {
          setEntries(prev => [msg.entry, ...prev].slice(0, 500));
        }
      } catch { /* ignore */ }
    };

    return () => { ws.close(); };
  }, []);

  const clear = () => setEntries([]);

  const filtered = entries.filter(e => {
    if (filterLevel !== 'all' && e.level !== filterLevel) return false;
    if (filterPath && !e.path.includes(filterPath) && !pathLabel(e.path).includes(filterPath)) return false;
    return true;
  });

  return (
    <div>
      <SectionTitle title="Monitor Agente" subtitle="Comunicacion en tiempo real entre agentes on-premise y el backend. Solo visible para admin y owner." />

      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: connected ? '#22C55E' : '#EF4444' }} />
          <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{connected ? 'Conectado' : 'Desconectado'}</span>
        </div>
        <div style={{ flex: 1 }} />
        <select value={filterLevel} onChange={e => setFilterLevel(e.target.value as 'all'|'ok'|'error')}
          style={{ padding: '4px 8px', fontSize: 11, borderRadius: 6, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)', color: 'var(--color-text-primary)', cursor: 'pointer' }}>
          <option value="all">Todos</option>
          <option value="ok">Solo OK</option>
          <option value="error">Solo Errores</option>
        </select>
        <input value={filterPath} onChange={e => setFilterPath(e.target.value)} placeholder="Filtrar por path o endpoint..."
          style={{ padding: '4px 10px', fontSize: 11, borderRadius: 6, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)', color: 'var(--color-text-primary)', outline: 'none', width: 200 }} />
        <button onClick={() => setPaused(v => !v)}
          style={{ padding: '4px 10px', fontSize: 11, borderRadius: 6, border: '1px solid var(--color-border-secondary)', background: paused ? '#FEF2E7' : 'transparent', color: paused ? '#7A4515' : 'var(--color-text-secondary)', cursor: 'pointer', fontWeight: 600 }}>
          {paused ? '▶ Reanudar' : '⏸ Pausar'}
        </button>
        <button onClick={clear}
          style={{ padding: '4px 10px', fontSize: 11, borderRadius: 6, border: '1px solid var(--color-border-secondary)', background: 'transparent', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
          🗑 Limpiar
        </button>
        <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{filtered.length} / {entries.length}</span>
      </div>

      {/* Log list */}
      <div style={{ border: '1px solid var(--color-border-tertiary)', borderRadius: 10, overflow: 'hidden', background: 'var(--color-background-primary)' }}>
        {/* Header */}
        <div style={{ display: 'grid', gridTemplateColumns: '140px 60px 1fr 60px 70px', gap: 10, padding: '8px 14px', background: 'var(--color-background-secondary)', borderBottom: '1px solid var(--color-border-tertiary)', fontSize: 10, fontWeight: 700, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          <span>Timestamp</span><span>Método</span><span>Endpoint</span><span>Status</span><span>Duración</span>
        </div>

        {/* Rows */}
        <div style={{ maxHeight: 'calc(100vh - 380px)', overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 13 }}>
              {entries.length === 0 ? 'Esperando comunicación de agentes...' : 'Sin resultados con los filtros aplicados'}
            </div>
          ) : filtered.map(e => (
            <div key={e.id} style={{ borderBottom: '1px solid var(--color-border-tertiary)', padding: '8px 14px', background: e.level === 'error' ? 'rgba(252,235,235,0.4)' : 'transparent' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '140px 60px 1fr 60px 70px', gap: 10, alignItems: 'center' }}>
                <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)', fontFamily: 'monospace' }}>{e.ts.replace('T',' ').replace('Z','')}</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: METHOD_COLOR[e.method] ?? '#534AB7' }}>{e.method}</span>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-primary)' }}>{pathLabel(e.path)}</div>
                  {e.agent_id && <div style={{ fontSize: 9, color: 'var(--color-text-tertiary)', fontFamily: 'monospace' }}>{e.agent_id.slice(0,8)}...</div>}
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: e.level === 'error' ? '#7A1515' : '#0F6E56' }}>{e.status}</span>
                <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{e.duration_ms}ms</span>
              </div>
              {/* Request / Response expandibles */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 4 }}>
                <div>
                  <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--color-text-tertiary)', textTransform: 'uppercase' }}>Request</span>
                  <JsonViewer data={e.request} />
                </div>
                <div>
                  <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--color-text-tertiary)', textTransform: 'uppercase' }}>Response</span>
                  <JsonViewer data={e.response} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main SettingsModule ──────────────────────────────────────────────────────


// ─── AI Config Section ───────────────────────────────────────────────────────

interface AIProvider {
  label: string;
  models: { id: string; label: string }[];
  key_hint: string;
  docs_url: string;
}

interface AIConfigData {
  provider: string;
  model: string;
  api_key_set: boolean;
  api_key_hint: string | null;
  azure_endpoint: string | null;
  azure_deployment: string | null;
  providers: Record<string, AIProvider>;
}

interface AITestResult {
  ok: boolean;
  latency_ms: number;
  model_used: string;
  error?: string;
}

function AIConfigSection({ onToast }: { onToast: (m: string) => void }) {
  const [config, setConfig] = React.useState<AIConfigData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [testResult, setTestResult] = React.useState<AITestResult | null>(null);

  const [provider, setProvider] = React.useState('openai');
  const [model, setModel] = React.useState('gpt-4o');
  const [apiKey, setApiKey] = React.useState('');
  const [showKey, setShowKey] = React.useState(false);
  const [azureEndpoint, setAzureEndpoint] = React.useState('');
  const [azureDeployment, setAzureDeployment] = React.useState('');

  React.useEffect(() => {
    setLoading(true);
    settingsApi.getAIConfig()
      .then(data => {
        const d = data as unknown as AIConfigData;
        setConfig(d);
        setProvider(d.provider);
        setModel(d.model);
        setAzureEndpoint(d.azure_endpoint || '');
        setAzureDeployment(d.azure_deployment || '');
      })
      .catch(() => onToast('Error al cargar configuración de IA'))
      .finally(() => setLoading(false));
  }, []);

  const providerInfo = config?.providers?.[provider];
  const models = providerInfo?.models || [];

  // Sincronizar model cuando llegue config o cambie provider
  React.useEffect(() => {
    if (!config) return;
    const available = config.providers?.[provider]?.models || [];
    if (available.length && !available.find(m => m.id === model)) {
      setModel(available[0].id);
    }
  }, [config, provider]);

  // Cuando cambia provider, seleccionar primer modelo válido
  const handleProviderChange = (p: string) => {
    setProvider(p);
    setTestResult(null);
    const firstModel = config?.providers?.[p]?.models?.[0]?.id || '';
    setModel(firstModel);
  };

  const handleSave = async () => {
    setSaving(true);
    setTestResult(null);
    try {
      await settingsApi.saveAIConfig({ provider, model, api_key: apiKey || undefined, azure_endpoint: azureEndpoint || undefined, azure_deployment: azureDeployment || undefined });
      onToast('Configuración de IA guardada');
      setApiKey('');
      // Recargar para actualizar hint
      const updated = await settingsApi.getAIConfig() as unknown as AIConfigData;
      setConfig(updated);
    } catch {
      onToast('Error al guardar configuración de IA');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await settingsApi.testAIConfig() as unknown as AITestResult;
      setTestResult(result);
    } catch {
      setTestResult({ ok: false, latency_ms: 0, model_used: model, error: 'Error de red al probar conexión' });
    } finally {
      setTesting(false);
    }
  };

  const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-primary)', color: 'var(--color-text-primary)', fontSize: 13 };
  const selectStyle = { ...inputStyle, cursor: 'pointer' };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 5, display: 'block' };

  const PROVIDER_ICONS: Record<string, string> = { openai: '🟢', anthropic: '🟠', gemini: '🔵', azure: '🔷' };

  if (loading) return (
    <div style={{ padding: 32, display: 'flex', alignItems: 'center', gap: 10, color: 'var(--color-text-secondary)', fontSize: 13 }}>
      <span>Cargando configuración…</span>
    </div>
  );

  return (
    <div style={{ padding: '24px 28px', maxWidth: 620 }}>
      <SectionTitle title="Inteligencia Artificial" subtitle="Configura el proveedor LLM para la generación automática de casos de prueba" />

      {/* Banner de estado */}
      {config && (
        config.api_key_set ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 10, background: 'linear-gradient(90deg,#e6f9ee 0%,#f0fdf4 100%)', border: '1px solid #a7f3d0', marginBottom: 24 }}>
            <span style={{ fontSize: 22 }}>✅</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#065f46' }}>Configuración activa</div>
              <div style={{ fontSize: 12, color: '#047857', marginTop: 2 }}>
                {config.providers?.[config.provider]?.label || config.provider}
                {' · '}
                {config.providers?.[config.provider]?.models?.find(m => m.id === config.model)?.label || config.model}
                {config.api_key_hint && <span> · <code style={{ background: '#d1fae5', padding: '1px 6px', borderRadius: 4, fontSize: 11 }}>{config.api_key_hint}</code></span>}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 10, background: 'linear-gradient(90deg,#fffbeb 0%,#fefce8 100%)', border: '1px solid #fcd34d', marginBottom: 24 }}>
            <span style={{ fontSize: 22 }}>⚠️</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>Sin API key configurada</div>
              <div style={{ fontSize: 12, color: '#b45309', marginTop: 2 }}>La generación automática de casos de IA no funcionará hasta que configures una clave.</div>
            </div>
          </div>
        )
      )}

      {/* Proveedor */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Proveedor</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
          {config && Object.entries(config.providers).map(([key, info]) => {
            const active = provider === key;
            return (
              <div key={key} onClick={() => handleProviderChange(key)}
                style={{ padding: '12px 14px', borderRadius: 10, border: `2px solid ${active ? '#185FA5' : 'var(--color-border-secondary)'}`, background: active ? '#185FA510' : 'var(--color-background-secondary)', cursor: 'pointer', transition: 'all 0.15s' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 18 }}>{PROVIDER_ICONS[key] || '🤖'}</span>
                  {config?.api_key_set && config.provider === key && (
                    <span style={{ fontSize: 10, fontWeight: 700, background: '#185FA5', color: '#fff', padding: '2px 7px', borderRadius: 20, letterSpacing: 0.3 }}>● ACTIVO</span>
                  )}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: active ? '#185FA5' : 'var(--color-text-primary)', marginTop: 4 }}>{info.label}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{info.key_hint}</div>
              </div>
            );
          })}
        </div>
        {providerInfo?.docs_url && (
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--color-text-tertiary)' }}>
            Obtén tu API key en{' '}
            <a href={providerInfo.docs_url} target="_blank" rel="noopener noreferrer" style={{ color: '#185FA5' }}>{providerInfo.docs_url}</a>
          </div>
        )}
      </div>

      {/* Modelo */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Modelo</label>
        <select value={model} onChange={e => setModel(e.target.value)} style={selectStyle}>
          {models.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
      </div>

      {/* Campos específicos Azure */}
      {provider === 'azure' && (
        <>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Endpoint de Azure OpenAI</label>
            <input value={azureEndpoint} onChange={e => setAzureEndpoint(e.target.value)} placeholder="https://mi-recurso.openai.azure.com" style={inputStyle} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>Nombre del Deployment</label>
            <input value={azureDeployment} onChange={e => setAzureDeployment(e.target.value)} placeholder="gpt-4o" style={inputStyle} />
          </div>
        </>
      )}

      {/* API Key */}
      <div style={{ marginBottom: 24 }}>
        <label style={labelStyle}>API Key</label>
        <div style={{ position: 'relative' }}>
          <input
            type={showKey ? 'text' : 'password'}
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            placeholder={config?.api_key_set ? `Configurada (${config.api_key_hint}) — deja vacío para mantener` : `Pega tu API key aquí (${providerInfo?.key_hint || ''})`}
            style={{ ...inputStyle, paddingRight: 40 }}
          />
          <button onClick={() => setShowKey(v => !v)}
            style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: 'var(--color-text-tertiary)' }}>
            {showKey ? '🙈' : '👁️'}
          </button>
        </div>
        {config?.api_key_set && (
          <div style={{ marginTop: 6, fontSize: 11, color: '#22863a', display: 'flex', alignItems: 'center', gap: 5 }}>
            <span>✅</span>
            <span>API key configurada: <code style={{ background: 'var(--color-background-secondary)', padding: '1px 5px', borderRadius: 4 }}>{config.api_key_hint}</code></span>
          </div>
        )}
      </div>

      {/* Acciones */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={handleSave} disabled={saving}
          style={{ padding: '8px 20px', borderRadius: 8, background: saving ? '#ccc' : '#185FA5', color: '#fff', border: 'none', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 13 }}>
          {saving ? 'Guardando…' : 'Guardar configuración'}
        </button>
        <button onClick={handleTest} disabled={testing || !config?.api_key_set}
          style={{ padding: '8px 16px', borderRadius: 8, background: 'var(--color-background-secondary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-secondary)', cursor: (testing || !config?.api_key_set) ? 'not-allowed' : 'pointer', fontSize: 13 }}>
          {testing ? '⏳ Probando…' : '⚡ Probar conexión'}
        </button>
      </div>

      {/* Resultado del test */}
      {testResult && (
        <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 10, background: testResult.ok ? '#d4edda' : '#f8d7da', border: `1px solid ${testResult.ok ? '#c3e6cb' : '#f5c6cb'}` }}>
          {testResult.ok ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>✅</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#155724' }}>Conexión exitosa</div>
                <div style={{ fontSize: 12, color: '#155724' }}>Modelo: <strong>{testResult.model_used}</strong> · Latencia: <strong>{testResult.latency_ms}ms</strong></div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span style={{ fontSize: 18 }}>❌</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#721c24' }}>Error de conexión</div>
                <div style={{ fontSize: 12, color: '#721c24' }}>{testResult.error}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Nota informativa */}
      <div style={{ marginTop: 28, padding: '12px 14px', borderRadius: 10, background: 'var(--color-background-secondary)', border: '1px solid var(--color-border-tertiary)', fontSize: 12, color: 'var(--color-text-tertiary)' }}>
        <strong style={{ color: 'var(--color-text-secondary)' }}>ℹ️ Nota:</strong> La API key se cifra con Fernet antes de almacenarse. Si no hay una <code>ENCRYPTION_KEY</code> en el servidor, se guarda sin cifrado. La configuración aquí siempre tiene prioridad sobre el archivo <code>.env</code>.
      </div>
    </div>
  );
}

const NAV_ITEMS: { key: Section; label: string; icon: string }[] = [
  { key: 'organization',   label: 'Organización',   icon: '🏢' },
  { key: 'users',          label: 'Usuarios',        icon: '👥' },
  { key: 'security',       label: 'Seguridad',       icon: '🔒' },
  { key: 'integrations',   label: 'Integraciones',   icon: '🔌' },
  { key: 'notifications',  label: 'Notificaciones',  icon: '🔔' },
  { key: 'ai',             label: 'Inteligencia Artificial', icon: '🤖' },
  { key: 'monitor',        label: 'Monitor Agente',  icon: '📡' },
];

export function SettingsModule() {
  const [section, setSection] = useState<Section>('organization');
  const [toast, setToast] = useState<string | null>(null);

  const notify = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }, []);

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 120px)', borderRadius: 14, border: '1px solid var(--color-border-tertiary)', overflow: 'hidden', background: 'var(--color-background-primary)' }}>

      {/* LEFT: vertical nav */}
      <div style={{ width: 210, borderRight: '1px solid var(--color-border-tertiary)', display: 'flex', flexDirection: 'column', background: 'var(--color-background-secondary)', flexShrink: 0 }}>
        <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid var(--color-border-tertiary)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Configuración</div>
        </div>

        <div style={{ padding: 8, flex: 1 }}>
          {NAV_ITEMS.map(item => {
            const active = section === item.key;
            return (
              <div key={item.key} onClick={() => setSection(item.key)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 8, marginBottom: 3, cursor: 'pointer', background: active ? 'var(--color-background-primary)' : 'transparent', border: active ? '1px solid var(--color-border-secondary)' : '1px solid transparent', transition: 'all 0.15s' }}>
                <span style={{ fontSize: 16 }}>{item.icon}</span>
                <span style={{ fontSize: 13, fontWeight: active ? 600 : 400, color: active ? '#185FA5' : 'var(--color-text-secondary)' }}>{item.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* RIGHT: section content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
        {section === 'organization'  && <OrganizationSection onToast={notify} />}
        {section === 'users'         && <UsersSection onToast={notify} />}
        {section === 'security'      && <SecuritySection onToast={notify} />}
        {section === 'integrations'  && <IntegrationsSection onToast={notify} />}
        {section === 'notifications' && <NotificationsSection onToast={notify} />}
        {section === 'monitor'        && <AgentMonitorSection />}
        {section === 'ai'            && <AIConfigSection onToast={notify} />}
      </div>

      {toast && <Toast msg={toast} onDone={() => setToast(null)} />}
    </div>
  );
}